import { LibraryObjectState } from '@breakoutlearning/firebase-repository/types'
import { observer } from 'mobx-react-lite'
import classNames from 'classnames'
import { useWalkthroughDialog } from '../dialogs/WalkthroughDialog.hooks'
import { useStudentAssignmentCubit } from 'hooks/cubits/studentAssignment'
import { LockOn } from 'components/icons/LockOn'
import { BreakoutButton } from 'components/design-system/BreakoutButton'
import { BreakoutTooltip } from 'components/design-system/BreakoutTooltip'
import { ValidLibraryObjectActionState } from '@breakoutlearning/firebase-repository/stores/ValidLibraryObject'
import { RoomStateStatus } from '@breakoutlearning/firebase-repository/models/RoomState'
import { useTranslationTyped } from 'i18n/i18n'
import { SpinnerCentered } from 'components/Spinner'
import { CompletedView } from './CompletedView'
import { ExpiredView } from './ExpiredView'
import { StudentPeerReviewController } from '@breakoutlearning/firebase-repository/cubits/StudentAssignment/StudentPeerReviewController'
import { useCubitBuilder } from 'hooks/cubits'
import { useEffect } from 'react'
import { when } from 'mobx'
import { useBreakoutUser } from 'hooks/profile'

export const StudentAssignmentStep3 = observer(function StudentAssignmentStep3({
  showStep2Title,
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement> & {
  showStep2Title: boolean
}) {
  const cubit = useStudentAssignmentCubit()
  const { user } = useBreakoutUser()
  const notYetAvailable = cubit.assignment.assignedAtIsInFuture

  const { t, tt } = useTranslationTyped()
  const { showWalkthroughDialogIfNecessary } = useWalkthroughDialog({
    roomId: cubit.roomStateId,
    roomStateStatus: cubit.libraryObject.roomStateStatus,
  })

  const peerReviewController = useCubitBuilder(() => {
    const controller = new StudentPeerReviewController(cubit.repository, {
      roomId: cubit.roomStateId ?? '',
      userId: cubit.repository.uid,
      sectionId: cubit.sectionId,
      assignmentId: cubit.assignmentId,
      userIdsInRoom: cubit.roomStateUserIds,
      peerReviewExcludesSelfReview:
        cubit.assignment.data.peerReviewExcludesSelfReview ?? false,
    })
    return controller
  }, [
    cubit.roomStateId,
    cubit.repository.uid,
    cubit.sectionId,
    cubit.assignmentId,
    cubit.roomStateUserIds,
    cubit.assignment.data.peerReviewExcludesSelfReview,
  ])

  // Delay initialization to prevent race conditions when switching between rooms
  useEffect(() => {
    const userInSection = cubit.section.data.userIds.includes(user.id)

    // if user is not in section, probably admin/instructor trying to view page and they will never init controller
    // so lets just initialize now, if an unauthorized user lands on this page they will get redirected
    if (!userInSection) return peerReviewController.initializeAsReviewer()

    const disposer = when(
      () => cubit.userIsInCurrentRoom,
      () => {
        peerReviewController.initializeAsReviewer()
      }
    )

    return () => disposer()
  }, [
    cubit.roomStateId,
    cubit.section.data.userIds,
    cubit.userIsInCurrentRoom,
    peerReviewController,
    user.id,
  ])

  const { libraryObjectState, canJoinTimeFormatted } = cubit.libraryObject

  const userYetToCompletePeerReview =
    peerReviewController.userYetToCompletePeerReview

  if (userYetToCompletePeerReview.isLoading) {
    return <SpinnerCentered />
  }

  const hasOutstandingPeerReview =
    userYetToCompletePeerReview.value && !!cubit.roomStateId
  const actionState = cubit.libraryObject.getActionState({
    hasOutstandingPeerReview,
  })

  if (libraryObjectState === LibraryObjectState.completed) {
    return (
      <>
        {!cubit.roomStateId && <SpinnerCentered />}
        {cubit.roomStateId && (
          <CompletedView
            actionState={actionState}
            peerReviewController={peerReviewController}
          />
        )}
      </>
    )
  }

  if (libraryObjectState === LibraryObjectState.expired) {
    return <ExpiredView />
  }

  const roleIssue = cubit.roleIssueForRoomState
  const minGroupSizeIssue = cubit.minGroupSizeIssueForRoomState

  const isSuspended =
    cubit.libraryObject.roomStateStatus === RoomStateStatus.suspended

  const startable =
    actionState === ValidLibraryObjectActionState.startSession &&
    !roleIssue &&
    !minGroupSizeIssue

  const joinable =
    (actionState === ValidLibraryObjectActionState.joinSession ||
      (isSuspended && cubit.roomState.canStart)) &&
    !roleIssue &&
    !minGroupSizeIssue

  const showDisabledJoinButton = !startable && !joinable

  const buttonAndTitleText = (() => {
    if (isSuspended) return tt.student_assignment.continue_session()
    if (startable) return tt.student_assignment.start_session()
    return tt.student_assignment.join_session()
  })()

  let tooltip: string | undefined

  if (showDisabledJoinButton) {
    if (notYetAvailable && cubit.assignment.assignedAtDate) {
      tooltip = tt.student_assignment.join_disabled_not_yet_available({
        date: cubit.assignment.formattedAssignedAtWithTime,
      })
    } else if (actionState === ValidLibraryObjectActionState.joinGroup) {
      tooltip = tt.student_assignment.join_disabled_join_group()
    } else if (
      cubit.roomState.isNotEmpty &&
      (!cubit.roomState.isScheduled ||
        [
          RoomStateStatus.mustSchedule,
          RoomStateStatus.mustScheduleSuspended,
        ].includes(cubit.roomStateStatus))
    ) {
      const studentScheduling =
        !cubit.assignment.data.forbidStudentGroupScheduling
      tooltip = studentScheduling
        ? tt.student_assignment.join_disabled_please_schedule()
        : tt.student_assignment.join_disabled_awaiting_instructor_schedule()
    } else if (actionState === ValidLibraryObjectActionState.completeQuiz) {
      tooltip = tt.student_assignment.quiz_required()
    } else if (cubit.roomState.isScheduled && !cubit.roomState.canStart) {
      tooltip = tt.student_assignment.join_disabled_starts_at({
        startsAt: canJoinTimeFormatted,
      })
    } else if (roleIssue) {
      const { min, issue } = roleIssue
      switch (issue) {
        case 'belowMinUsers':
          tooltip = tt.student_assignment.join_disabled_below_min_users({ min })
          break
        case 'unassignedUsers':
        case 'invalidRoles':
        case 'aboveMaxUsers':
          tooltip = tt.student_assignment.join_disabled_invalid_roles()
      }
    } else if (minGroupSizeIssue) {
      tooltip = tt.student_assignment.join_disabled_below_min_users({
        min: minGroupSizeIssue.min,
      })
    }
  }

  return (
    <div className={classNames(className)} {...props}>
      <div className="text-body-medium text-grey-text">
        {showStep2Title
          ? t('student_assignment.step_2')
          : t('student_assignment.step_3')}
      </div>
      <h2 className="mt-3 text-title-large">
        {buttonAndTitleText}
        {notYetAvailable && (
          <LockOn size={16} className="ml-1 inline stroke-grey-text" />
        )}
      </h2>
      {notYetAvailable && cubit.assignment.assignedAtDate ? (
        <p className="mt-1 text-body-medium">
          {tt.student_assignment.session_available_on_date({
            date: cubit.assignment.formattedAssignedAtWithTime,
          })}
        </p>
      ) : (
        <p className="mt-1 text-body-medium">
          {t('dart.join_session_description')}
        </p>
      )}
      <div className="mt-3">
        {showDisabledJoinButton &&
          (tooltip ? (
            <BreakoutTooltip content={tooltip}>
              <div>
                <BreakoutButton data-testid="join-session-button" disabled>
                  {buttonAndTitleText}
                </BreakoutButton>
              </div>
            </BreakoutTooltip>
          ) : (
            <BreakoutButton data-testid="join-session-button" disabled>
              {buttonAndTitleText}
            </BreakoutButton>
          ))}
        {(joinable || startable) && (
          <BreakoutButton
            data-testid="join-session-button"
            disabled={!cubit.roomState.isScheduled || !cubit.roomStateId}
            onClick={async () => {
              if (!cubit.allPreWorkQuestionsAnswered) return
              if (!cubit.roomState.isScheduled) return

              showWalkthroughDialogIfNecessary(() => {
                cubit.logEvent('room_join')
                cubit.setJoiningSession()
              })
            }}
            className="inline-block w-40 rounded-2xl bg-core-primary px-8 py-2 text-label-large text-core-on-primary disabled:opacity-50"
          >
            {buttonAndTitleText}
          </BreakoutButton>
        )}
      </div>
    </div>
  )
})
